import { OrangeStripeSectionInterface, SettingsInterface, WebsiteUrlInterface } from '@on-arte/core-types';
import { HeaderMenuTheme, InfoBar, Logger, UseLocalStorage, useLocalStorage, UseLogger, useLogger, UseState } from '@on-arte/ui';
import { Location } from 'history';
import { Match, match, MatchFunction } from 'path-to-regexp';
import React, { useEffect, useMemo, useReducer, useState } from 'react';
import { TransProps, useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { useLocation } from 'react-router-dom';

import { getRoutingDetails, getSettings } from '@onArte/frontend/api/requests';
import { appConfig } from '@onArte/frontend/app.config';
import { AuthContext, RoutingContext } from '@onArte/frontend/contexts';
import { BasketSidePanelContext } from '@onArte/frontend/contexts/basketSidePanel.context';
import { LocalStorageKey, QueryKey } from '@onArte/frontend/enums';
import { AuthContextState, RoutingState } from '@onArte/frontend/interfaces';
import { FrontendApiError } from '@onArte/frontend/models';
import { authReducer } from '@onArte/frontend/reducers';
import { AuthContextData } from '@onArte/frontend/types';
import { plRouting } from '@onArte/shared/constants';
import { RouteNameEnum, WebsiteUrlType } from '@onArte/shared/enums';
import { RouteInfo } from '@onArte/shared/interfaces';
import {
  buildRouteTitle,
  getRouteDetailsByName,
  getRouteDetailsByUrl,
  homeHeading,
  resolveArtworkEntityRoute,
  stripArtworkCategoryPath
} from '@onArte/shared/utils';

import { CookiesPopup } from '../cookiesPopup/cookiesPopup.component';
import { FooterComponent } from '../footerComponent/footerComponent.component';
import { HeaderMenu } from '../headerMenu/headerMenu.component';

import {
  pathsWithDynamicRouting,
  pathsWithFooterHidden,
  pathsWithHeaderBorderBottom,
  pathsWithHeaderMenuHidden,
  pathsWithHeaderMenuTransparentTheme,
  pathsWithInfoBarHidden,
  pathsWithTopBorderInFooter
} from './appWrapper.configs';
import { ChildrenContainer, HeaderWrapper } from './appWrapper.styled';
import { AppWrapperProps } from './appWrapper.types';

/**
 * Trasy, na których tytuł ustawia sam widok, z danych encji.
 *
 * Bez tej listy `AppWrapper` nadpisywałby po podłączeniu Reacta tytuł wyliczony
 * przez serwer i użytkownik widziałby w karcie przeglądarki „Szczegóły dzieła",
 * podczas gdy wyszukiwarka dostawała „Nazwa dzieła – Artysta | on*arte".
 * Rozjazd nie psuł kryterium odbioru, bo ono mierzy odpowiedź HTTP — ale przy
 * odbiorze wyglądałby jak praca niewykonana.
 *
 * Widoki ustawiają tytuł w swoich efektach; efekty dziecka wykonują się przed
 * efektem rodzica, więc gdyby `AppWrapper` nadal go ustawiał, i tak wygrałby on.
 */
const routesManagingOwnTitle: RouteNameEnum[] = [
  RouteNameEnum.Home,
  RouteNameEnum.ArtworkDetails,
  RouteNameEnum.ArtworkDetailsInCategory,
  RouteNameEnum.ArtistDetails,
  RouteNameEnum.Collection,
];

export const AppWrapper: React.FC<AppWrapperProps> = (props: AppWrapperProps): JSX.Element => {
  const { children, token, initialRoutingState }: AppWrapperProps = props;
  const { t }: TransProps<never> = useTranslation();
  const { logger }: UseLogger = useLogger();
  const location: Location = useLocation();
  const pathObject: RouteInfo | undefined = getRouteDetailsByUrl(location.pathname) ?? getRouteDetailsByName(RouteNameEnum.NotFound);
  const [isBasketSidePanelOpen, setIsBasketSidePanelOpen]: UseState<boolean> = useState<boolean>(false);
  const [orangeStripeData, setOrangeStripeData]: UseState<OrangeStripeSectionInterface | null> 
    = useState<OrangeStripeSectionInterface | null>(null);
  const [routingState, setRoutingState]: UseState<RoutingState> = useState<RoutingState>(initialRoutingState ?? {
    routes: plRouting, elementParamsFromRouting: {}, currentWebsiteUrl: undefined, currentRouteObject: undefined
  });
  const [authData]: UseLocalStorage<AuthContextState> = useLocalStorage<AuthContextState>(
    LocalStorageKey.AuthData, { userData: null, token: null, tokenExpiration: null }
  );
  const [authState, authDispatch]: AuthContextData = useReducer(
    authReducer,
    authData
      ? { userData: authData.userData, token: authData.token, tokenExpiration: authData.tokenExpiration }
      : { userData: null, token: null, tokenExpiration: null },
  );
  
  const valueForAuthContext: AuthContextData = useMemo((): AuthContextData => [
    { ...authState, ...(token ? { token } : {})},
    authDispatch
  ], [authState, token]);

  const setLocalUrlToState: (urlMatch: MatchFunction<Record<string, string>>, pathObjectValue: RouteInfo) => void = (
    urlMatch: MatchFunction<Record<string, string>>, pathObjectValue: RouteInfo
  ): void => {
    const localUrlMatch: Match<Record<string, string>> | false = urlMatch(location.pathname);
    if (localUrlMatch) {
      setRoutingState({
        ...routingState,
        currentWebsiteUrl: undefined,
        elementParamsFromRouting: localUrlMatch?.params,
        currentRouteObject: pathObjectValue
      });
    }
  };

  useQuery(
    [QueryKey.Settings],
    (): Promise<SettingsInterface> => getSettings(),
    {
      onSuccess: (data: SettingsInterface): void => setOrangeStripeData(data.orangeStripeSection),
      onError: (error: FrontendApiError): void => logger(QueryKey.Settings, error)
    }
  );
  
  useEffect(
    (): void => {
      setTimeout((): void => window.scrollTo(0, 0), 0);

      // Gdy stan routingu został już wyliczony przez serwer dla TEGO adresu, nie ma
      // czego ustalać. Bez tego warunku efekt zaraz po podłączeniu wyczyściłby stan
      // i odpytał API, a użytkownik zobaczyłby, jak treść znika i pojawia się ponownie.
      // Przy zwykłej nawigacji w obrębie aplikacji warunek nie zachodzi i wszystko
      // działa jak dotąd.
      // Adres z segmentem kategorii (poz. 13) porównujemy i rozwiązujemy w formie
      // bez segmentu — tylko taką zna słownik ładnych adresów.
      if (routingState.currentWebsiteUrl?.prettyUrl === stripArtworkCategoryPath(location.pathname)) {
        if (pathObject && !routesManagingOwnTitle.includes(pathObject.name)) {
          document.title = buildRouteTitle(t(pathObject.title));
        }

        return;
      }

      setRoutingState({ ...routingState, currentWebsiteUrl: undefined, elementParamsFromRouting: {}, currentRouteObject: undefined });

      if (pathObject) {
        if (!routesManagingOwnTitle.includes(pathObject.name)) {
          document.title = buildRouteTitle(t(pathObject.title));
        }

        if (pathsWithDynamicRouting.includes(pathObject.name)) {
          // Trasa z kategorią wskazuje ten sam byt co ArtworkDetails — adres
          // rzeczywisty (/dziela/{id}) dopasowujemy wzorcem trasy bazowej.
          const urlMatch: MatchFunction<Record<string, string>> = match(resolveArtworkEntityRoute(pathObject).url);
          getRoutingDetails(stripArtworkCategoryPath(location.pathname), WebsiteUrlType.Both)
            .then((route: WebsiteUrlInterface | undefined): void => {
              if (route) {
                const realUrlMatch: Match<Record<string, string>> | false = urlMatch(route.realUrl);
                if (realUrlMatch) {
                  setRoutingState({
                    ...routingState,
                    currentWebsiteUrl: route,
                    elementParamsFromRouting: realUrlMatch?.params,
                    currentRouteObject: pathObject
                  });
                }
              } else {
                setLocalUrlToState(urlMatch, pathObject);
              }
            })
            .catch((): void => setLocalUrlToState(urlMatch, pathObject));
        }
      }
    },
    [location.pathname]
  );
  
  const isOrangeStripeUrlExternal: boolean | undefined = useMemo(
    (): boolean | undefined => {
      return orangeStripeData?.link.slice(0, 4) === 'http';
    },
    [orangeStripeData]
  );

  return (
    <RoutingContext.Provider value={routingState}>
      <Logger
        applicationId={appConfig.loggerSettings.applicationId}
        clientToken={appConfig.loggerSettings.clientToken}
        service={appConfig.loggerSettings.service}
        env={appConfig.loggerSettings.env}
        version={appConfig.loggerSettings.version}
        userId={authState.userData?.id}
      >
        <BasketSidePanelContext.Provider value={{isBasketSidePanelOpen, setIsBasketSidePanelOpen}}>
          <AuthContext.Provider value={valueForAuthContext}>
            {!!pathObject && !pathsWithInfoBarHidden.includes(pathObject.name) && !!orangeStripeData && (
              <InfoBar 
                content={orangeStripeData.text} 
                externalUrl={isOrangeStripeUrlExternal ? orangeStripeData.link : undefined}
                internalPath={!isOrangeStripeUrlExternal ? orangeStripeData.link : undefined}
              />
            )}
            {!!pathObject && !pathsWithHeaderMenuHidden.includes(pathObject.name) && (
              <HeaderWrapper>
                <HeaderMenu
                  headerMenuTheme={pathsWithHeaderMenuTransparentTheme.includes(pathObject.name)
                    ? HeaderMenuTheme.Transparent
                    : HeaderMenuTheme.SolidBeige
                  }
                  withBottomBorder={pathsWithHeaderBorderBottom.includes(pathObject.name)}
                  logoCaption={pathObject.name === RouteNameEnum.Home ? homeHeading : undefined}
                />
              </HeaderWrapper>
            )}
            <ChildrenContainer
              $withoutMarginTop={!!pathObject && pathsWithHeaderMenuTransparentTheme.includes(pathObject.name)}
            >
              {children}
            </ChildrenContainer>
            {!!pathObject && !pathsWithFooterHidden.includes(pathObject.name) && (
              <FooterComponent withTopBorder={pathsWithTopBorderInFooter.includes(pathObject.name)} />
            )}
            <CookiesPopup />
          </AuthContext.Provider>
        </BasketSidePanelContext.Provider>
      </Logger>
    </RoutingContext.Provider>
  );
};
